import { useCallback, useEffect, useMemo, useState } from "react";
import baseUrl from "../../api/baseUrl";
import {
  audienceOptionsForTemplate,
  DEFAULT_AUDIENCE_FILTER,
  formatAudienceFilterLabel,
  type SmsAudienceFilter,
} from "../myaccount/sms/smsSettingsConfig";

export type SmsCampaignType = "holiday" | "oral-health-tip" | "promo";

export type SmsCampaign = {
  id: string;
  type: SmsCampaignType;
  name: string;
  sendDate: string;
  sendTime?: string;
  audienceFilter: SmsAudienceFilter;
  message: string;
  status: string;
  skipReason?: string | null;
  stats?: {
    targeted?: number;
    sent?: number;
    failed?: number;
    estimatedTokens?: number;
  };
  sentAt?: string | null;
  requiresSendNow?: boolean;
};

const TYPE_META: Record<
  SmsCampaignType,
  { label: string; description: string; placeholders: string[] }
> = {
  holiday: {
    label: "Holiday greetings",
    description: "Schedule holiday messages for a specific date",
    placeholders: ["{{patient_name}}", "{{clinic_name}}", "{{holiday_name}}"],
  },
  "oral-health-tip": {
    label: "Oral health tips",
    description: "Schedule tip messages for a specific date",
    placeholders: ["{{patient_name}}", "{{clinic_name}}"],
  },
  promo: {
    label: "Promotional offers",
    description: "Schedule promotional offers for a specific date",
    placeholders: ["{{patient_name}}", "{{clinic_name}}", "{{offer_details}}"],
  },
};

const STATUS_STYLES: Record<string, string> = {
  scheduled: "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300",
  sending: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
  sent: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  partial: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
  failed: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
  skipped: "bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300",
  expired: "bg-slate-200 text-slate-500 dark:bg-slate-700 dark:text-slate-400",
};

const DEFAULT_SEND_TIME = "09:00";

const inputClassName =
  "w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-800 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-primary focus:ring-2 focus:ring-primary/15 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100";

const textareaClassName = `${inputClassName} min-h-[96px] resize-y leading-relaxed`;

function statusLabel(status: string) {
  if (status === "sent") return "Sent ✓";
  return status.charAt(0).toUpperCase() + status.slice(1);
}

function formatCampaignSchedule(sendDate: string, sendTime?: string) {
  const time = sendTime || DEFAULT_SEND_TIME;
  return `${sendDate} · ${time} Asia/Colombo`;
}

function CampaignTypeSection({
  type,
  campaigns,
  onChanged,
}: {
  type: SmsCampaignType;
  campaigns: SmsCampaign[];
  onChanged: () => void;
}) {
  const meta = TYPE_META[type];
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sendingId, setSendingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [sendDate, setSendDate] = useState("");
  const [sendTime, setSendTime] = useState(DEFAULT_SEND_TIME);
  const [message, setMessage] = useState("");
  const [audienceFilter, setAudienceFilter] = useState<SmsAudienceFilter>(
    DEFAULT_AUDIENCE_FILTER[type] || "active_1y"
  );
  const [estimate, setEstimate] = useState<{
    patientCount: number;
    segmentsPerMessage: number;
    estimatedTokens: number;
  } | null>(null);
  const [estimateLoading, setEstimateLoading] = useState(false);

  const options = useMemo(() => audienceOptionsForTemplate(type), [type]);

  const scheduledCount = campaigns.filter((c) => c.status === "scheduled").length;
  const sentCount = campaigns.filter((c) => c.status === "sent" || c.status === "partial").length;

  const refreshEstimate = useCallback(() => {
    if (!message.trim()) {
      setEstimate(null);
      return;
    }
    setEstimateLoading(true);
    baseUrl
      .post("/client/account/sms-campaigns/estimate", {
        type,
        message,
        audienceFilter,
      })
      .then((res) => {
        if (res?.data?.success) setEstimate(res.data.data);
        else setEstimate(null);
      })
      .catch(() => setEstimate(null))
      .finally(() => setEstimateLoading(false));
  }, [type, message, audienceFilter]);

  useEffect(() => {
    const t = setTimeout(refreshEstimate, 350);
    return () => clearTimeout(t);
  }, [refreshEstimate]);

  const resetForm = () => {
    setName("");
    setSendDate("");
    setSendTime(DEFAULT_SEND_TIME);
    setMessage("");
    setAudienceFilter(DEFAULT_AUDIENCE_FILTER[type] || "active_1y");
    setError(null);
    setShowForm(false);
  };

  const handleCreate = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await baseUrl.post("/client/account/sms-campaigns", {
        type,
        name: name.trim() || meta.label,
        sendDate,
        sendTime,
        message,
        audienceFilter,
      });
      if (!res?.data?.success) {
        setError(res?.data?.message || "Could not create campaign");
        return;
      }
      resetForm();
      onChanged();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        "Could not create campaign";
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleSendNow = async (id: string) => {
    setSendingId(id);
    setError(null);
    try {
      const res = await baseUrl.post(`/client/account/sms-campaigns/${id}/send-now`);
      if (!res?.data?.success) {
        setError(res?.data?.message || "Send failed");
        return;
      }
      onChanged();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        "Send failed";
      setError(msg);
    } finally {
      setSendingId(null);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Delete this scheduled campaign?")) return;
    try {
      await baseUrl.delete(`/client/account/sms-campaigns/${id}`);
      onChanged();
    } catch {
      setError("Could not delete campaign");
    }
  };

  return (
    <div className="rounded-2xl border border-slate-200/80 bg-slate-50/40 p-4 dark:border-slate-600/80 dark:bg-slate-900/30">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-slate-900 dark:text-white">{meta.label}</p>
          <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{meta.description}</p>
          <p className="mt-1 text-[11px] font-medium text-slate-500 dark:text-slate-400">
            {scheduledCount} scheduled · {sentCount} sent
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowForm((v) => !v)}
          className="rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-white"
        >
          {showForm ? "Cancel" : "Add campaign"}
        </button>
      </div>

      {error ? (
        <p className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-800 dark:bg-red-950/30 dark:text-red-300">
          {error}
        </p>
      ) : null}

      {showForm ? (
        <div className="mt-4 space-y-3 rounded-xl border border-slate-200 bg-white p-3 dark:border-slate-600 dark:bg-slate-900">
          <label className="block">
            <span className="mb-1 block text-xs font-semibold text-slate-600 dark:text-slate-300">
              Name
            </span>
            <input
              className={inputClassName}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={meta.label}
            />
          </label>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1 block text-xs font-semibold text-slate-600 dark:text-slate-300">
                Send date
              </span>
              <input
                type="date"
                className={inputClassName}
                value={sendDate}
                onChange={(e) => setSendDate(e.target.value)}
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-semibold text-slate-600 dark:text-slate-300">
                Send time
              </span>
              <input
                type="time"
                className={inputClassName}
                value={sendTime}
                onChange={(e) => setSendTime(e.target.value)}
              />
              <span className="mt-1 block text-[11px] text-slate-400 dark:text-slate-500">
                Asia/Colombo
              </span>
            </label>
          </div>
          <div className="space-y-1.5">
            <p className="text-xs font-semibold text-slate-600 dark:text-slate-300">Audience</p>
            {options.map((option) => (
              <label
                key={option.value}
                className="flex cursor-pointer items-center gap-2 text-xs text-slate-700 dark:text-slate-200"
              >
                <input
                  type="radio"
                  name={`${type}-audience-new`}
                  checked={audienceFilter === option.value}
                  onChange={() => setAudienceFilter(option.value)}
                />
                {option.label}
              </label>
            ))}
          </div>
          <label className="block">
            <span className="mb-1 block text-xs font-semibold text-slate-600 dark:text-slate-300">
              Message
            </span>
            <div className="mb-1.5 flex flex-wrap gap-1">
              {meta.placeholders.map((p) => (
                <span
                  key={p}
                  className="rounded-md bg-slate-100 px-2 py-0.5 font-mono text-[11px] text-slate-600 dark:bg-slate-700 dark:text-slate-300"
                >
                  {p}
                </span>
              ))}
            </div>
            <textarea
              className={textareaClassName}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Write the SMS message…"
            />
            <span className="mt-1 block text-[11px] text-slate-400 dark:text-slate-500">
              Press Enter for a new line.
            </span>
          </label>
          <p className="text-[11px] text-slate-500 dark:text-slate-400">
            {estimateLoading
              ? "Estimating cost…"
              : estimate
                ? `Estimated cost: ${estimate.estimatedTokens.toLocaleString()} tokens (${estimate.patientCount.toLocaleString()} patients × ${estimate.segmentsPerMessage} segment${estimate.segmentsPerMessage === 1 ? "" : "s"})`
                : "Enter a message to see estimated token cost"}
          </p>
          <button
            type="button"
            disabled={saving || !sendDate || !sendTime || !message.trim()}
            onClick={handleCreate}
            className="rounded-lg bg-primary px-3 py-2 text-xs font-semibold text-white disabled:opacity-50"
          >
            {saving ? "Saving…" : "Create campaign"}
          </button>
        </div>
      ) : null}

      <div className="mt-4 space-y-2">
        {campaigns.length === 0 ? (
          <p className="text-xs text-slate-400 dark:text-slate-500">No campaigns yet.</p>
        ) : (
          campaigns.map((campaign) => (
            <div
              key={campaign.id}
              className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 dark:border-slate-600 dark:bg-slate-900"
            >
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm font-semibold text-slate-800 dark:text-white">
                      {campaign.name}
                    </p>
                    <span
                      className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                        STATUS_STYLES[campaign.status] || STATUS_STYLES.scheduled
                      }`}
                    >
                      {statusLabel(campaign.status)}
                    </span>
                  </div>
                  <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
                    {formatCampaignSchedule(campaign.sendDate, campaign.sendTime)} ·{" "}
                    {formatAudienceFilterLabel(campaign.audienceFilter)}
                    {campaign.stats?.estimatedTokens != null
                      ? ` · ${campaign.stats.estimatedTokens} tokens est.`
                      : ""}
                  </p>
                  {campaign.skipReason ? (
                    <p className="mt-1 text-[11px] font-medium text-orange-700 dark:text-orange-300">
                      {campaign.skipReason}
                    </p>
                  ) : null}
                  {campaign.status === "sent" || campaign.status === "partial" ? (
                    <p className="mt-1 text-[11px] text-slate-500">
                      Sent {campaign.stats?.sent ?? 0} / {campaign.stats?.targeted ?? 0}
                      {(campaign.stats?.failed ?? 0) > 0
                        ? ` (${campaign.stats?.failed} failed)`
                        : ""}
                    </p>
                  ) : null}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {campaign.status === "scheduled" && campaign.requiresSendNow ? (
                    <button
                      type="button"
                      disabled={sendingId === campaign.id}
                      onClick={() => handleSendNow(campaign.id)}
                      className="rounded-md bg-primary px-2 py-1 text-[11px] font-semibold text-white disabled:opacity-50"
                    >
                      {sendingId === campaign.id ? "Sending…" : "Send now"}
                    </button>
                  ) : null}
                  {campaign.status === "scheduled" ? (
                    <button
                      type="button"
                      onClick={() => handleDelete(campaign.id)}
                      className="rounded-md bg-slate-100 px-2 py-1 text-[11px] font-semibold text-slate-600 dark:bg-slate-700 dark:text-slate-300"
                    >
                      Delete
                    </button>
                  ) : null}
                </div>
              </div>
              <p className="mt-2 line-clamp-3 whitespace-pre-wrap text-xs text-slate-600 dark:text-slate-300">
                {campaign.message}
              </p>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default function SmsCampaignsPanel() {
  const [campaigns, setCampaigns] = useState<SmsCampaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    return baseUrl
      .get("/client/account/sms-campaigns")
      .then((res) => {
        if (res?.data?.success) setCampaigns(res.data.data || []);
        else {
          setCampaigns([]);
          setError("Could not load campaigns");
        }
      })
      .catch(() => {
        setCampaigns([]);
        setError("Could not load campaigns");
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const byType = (type: SmsCampaignType) => campaigns.filter((c) => c.type === type);

  if (loading) {
    return <p className="text-sm text-slate-500">Loading campaigns…</p>;
  }

  return (
    <div className="space-y-4">
      {error ? (
        <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
      ) : null}
      <CampaignTypeSection type="holiday" campaigns={byType("holiday")} onChanged={load} />
      <CampaignTypeSection
        type="oral-health-tip"
        campaigns={byType("oral-health-tip")}
        onChanged={load}
      />
      <CampaignTypeSection type="promo" campaigns={byType("promo")} onChanged={load} />
    </div>
  );
}
